import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { combineLatest, first, firstValueFrom, Observable, of, switchMap } from 'rxjs';

import {
  EditorWorkflowNavigation,
  runtimeSelectors,
  RuntimeState,
} from '@blaze-runtime/engine-data-access';
import { CanvasElement, ConnectionChain, getNamespaceId } from '@blaze-runtime/engine-domain';
import { ServerErrorResponse } from '@blaze-shared/api-contracts-domain';
import { RuntimeMode } from '@blaze-shared/app-state-data-access';
import { Pagination } from '@blaze-shared/datasets-domain';
import {
  BULK_ACTION_IDS_PROP,
  CanvasElementDataProperties,
  ElementOutputUi,
  getActiveBinding,
  RuntimeEventContext,
} from '@blaze-shared/elements-domain';
import { IntelliSenseService } from '@blaze-shared/intellisense-feature';
import {
  getErrorMessage,
  NotificationService,
  NotificationType,
  syncSelect,
} from '@blaze-shared/platform-data-access';
import { PagesApiService } from '@blaze-shared/projects-data-access';
import { SchemaService } from '@blaze-shared/schema-data-access';
import { checkSameDatasetColumn, isActionEventType } from '@blaze-shared/schema-domain';
import {
  BlazeSchema,
  createElementSchemaId,
  DatasetColumn,
  DatasetSchema,
  getSchemaById,
  hasSourceColumnId,
  type RuntimeEventType,
} from '@blaze-shared/schema-domain';
import { isNullish } from '@blaze-shared/shared-util';
import { VariablesFacade } from '@blaze-shared/variables-data-access';
import {
  Connection,
  DatasetWorkflowNodeBase,
  findFirstNodeInWorkflow,
  findFirstNodeInWorkflowOfType,
  isApiCallNode,
  isComputeNode,
  isDatasetNode,
  isDeleteNode,
  isGetNode,
  isInsertNode,
  isJoinNode,
  isLookupNode,
  isUpdateNode,
  RuntimeElement,
  Workflow,
  WorkflowNode,
} from '@blaze-shared/workflow-domain';

import { selectAccountsDropdownEntities, selectElements } from '../+state/engine/engine.selectors';
import { RuntimeFacade } from '../+state/runtime/runtime.facade';
import { hasDataProps, hasProps } from '../canvas-elements/element-model/element-props-guards';
import { findRelatedNamespaces } from '../canvas-elements/element-model/namespaces';

@Injectable({ providedIn: 'root' })
export class RuntimeService {
  private readonly editorWorkflowNavigation = inject(EditorWorkflowNavigation);
  private readonly iss = inject(IntelliSenseService);
  private readonly notificationService = inject(NotificationService);
  private readonly pagesApiService = inject(PagesApiService);
  private readonly runtimeFacade = inject(RuntimeFacade);
  private readonly schemaService = inject(SchemaService);
  private readonly store = inject(Store<RuntimeState>);
  private readonly variablesFacade = inject(VariablesFacade);

  private readonly workflowPaginations = new Map<string, Pagination | undefined>();

  /**
   * Checks whether data inside the workflow can use server-side pagination.
   * @param workflow
   */
  canPaginateWorkflowData(workflow: Workflow | null): boolean {
    if (!workflow) {
      return false;
    }
    const nodes = workflow.nodes;
    const allowedNodeTypes = new Set(['Assign', 'Compute', 'Get', 'Navigate', 'StoreVariable']);
    let canPaginate = true;
    nodes.forEach((node) => {
      if (!allowedNodeTypes.has(node.nodeType)) {
        canPaginate = false;
      }
    });
    return canPaginate;
  }

  /**
   * Creates a chain of connections by appending the targeted connection to the list of connection nodes.
   */
  createConnectionChain(connection: Connection): ConnectionChain {
    const chainId = crypto.randomUUID();
    const chain: ConnectionChain = [];
    let currConnection: Connection | null = { ...connection };

    // Add fake source connection to ensure the autostart connection fetches necessary data.
    if (currConnection.autostart) {
      const prefix = 'autostart';
      chain.push({
        chainId,
        eventType: 'data-ready',
        id: `${prefix}-${currConnection.id}`,
        source: {
          id: '',
          layerType: 'Data',
        },
        target: { ...currConnection.source },
        autostart: !!currConnection.autostart,
        dataRefresh: !!currConnection.dataRefresh,
      });
    } else if (currConnection.source.layerType === 'Workflow' && currConnection.dataRefresh) {
      const prefix = 'dataRefresh';
      chain.push({
        chainId,
        eventType: 'data-ready',
        id: `${prefix}-${currConnection.id}`,
        source: {
          id: '',
          layerType: 'Data',
        },
        target: { ...currConnection.source },
        autostart: !!currConnection.autostart,
        dataRefresh: !!currConnection.dataRefresh,
      });
    }

    chain.push({ ...currConnection, chainId });
    // UI layer has to terminate the chain. This is because the UI needs time to update its view and dispatch an event
    // informing runtime what is the new default/selected value. The event from UI will initiate new connection chain.
    while (currConnection?.target?.id && currConnection?.target.layerType !== 'Ui') {
      currConnection = this.runtimeFacade.getConnectionBySource(currConnection.target);
      if (currConnection) {
        chain.push({ ...currConnection, chainId });
      }
    }
    return chain;
  }

  getConnectionNamespace(
    connection: Connection,
    defaultNamespace: string,
    relatedNamespaces: string[],
  ): string {
    if (!connection) {
      return defaultNamespace;
    }
    const { source, target } = connection;

    if (target.layerType === 'Ui') {
      const targetNs = getNamespaceId(target.id);
      return relatedNamespaces.includes(targetNs) ? targetNs : defaultNamespace;
    }

    if (target.layerType === 'Workflow') {
      const wf = this.runtimeFacade.workflows.find(({ id }) => id === target.id);
      // For WF page on which it's placed determines its namespace. If not found, we can check source element's ns.
      if (wf?.pageId && relatedNamespaces.includes(wf?.pageId)) {
        // Found pageId - use it as a namespace.
        return wf.pageId;
      } else if (
        source.layerType === 'Ui' &&
        relatedNamespaces.includes(getNamespaceId(source.id))
      ) {
        // Source is a Ui element whose namespace is found in related namespaces.
        return getNamespaceId(source.id);
      } else {
        return defaultNamespace;
      }
    }

    return defaultNamespace;
  }

  reset(): void {
    this.workflowPaginations.clear();
    this.runtimeFacade.reset();
    this.variablesFacade.clearVariablesInMemory();
  }

  getAutostartConnections(
    connections: Connection[],
    workflows: Workflow[],
    allowAllNodes = false,
    ignorePreventAutostart = false,
  ): Map<string, Connection[]> {
    const autostartConnections = new Map<string, Connection[]>();

    // Check each workflow to find the ones which start when runtime starts
    workflows.forEach((workflow) => {
      const startNode = workflow.nodes.find(({ id }) => id === workflow.startNodeId);
      // Only workflows which start with either Get or ApiCall can run automatically
      const nodeAllowed = allowAllNodes || this.isAutostartableNode(startNode);
      if ((ignorePreventAutostart === true || !workflow.preventAutoStart) && nodeAllowed) {
        // Check whether the workflow is a target of a connection
        const connectionTargetingWorkflow = connections.find(({ target }) => {
          return target.id === workflow.id && target.layerType === 'Workflow';
        });

        let connectionToStart =
          connectionTargetingWorkflow ??
          connections.find(({ source }) => {
            return source.id === workflow.id && source.layerType === 'Workflow';
          });
        if (connectionToStart) {
          connectionToStart = {
            ...connectionToStart,
            autostart: true,
            ignorePreventAutostart,
          };
          const ns = getNamespaceId(connectionToStart.id);
          const nsConnections = autostartConnections.get(ns) || [];
          nsConnections.push(connectionToStart);
          autostartConnections.set(ns, nsConnections);
        }
      }
    });
    return autostartConnections;
  }

  /**
   * Finds connections which should be added to the queue based on the specified workflows.
   * @param workflows
   * @returns
   */
  getDataChangedConnections(
    workflows: Workflow[],
    paginationRefresh = false,
  ): Map<string, Connection[]> {
    const connectionsToRefresh = new Map<string, Connection[]>();
    workflows.forEach((workflow) => {
      const connectionTargetingWorkflow = this.runtimeFacade.connections.find(({ target }) => {
        return target.id === workflow.id && target.layerType === 'Workflow';
      });
      let connectionToStart =
        connectionTargetingWorkflow ??
        this.runtimeFacade.connections.find(({ source }) => {
          return source.id === workflow.id && source.layerType === 'Workflow';
        });
      if (connectionToStart) {
        connectionToStart = { ...connectionToStart, dataRefresh: true, paginationRefresh };
        const ns = getNamespaceId(connectionToStart.id);
        const nsConnections = connectionsToRefresh.get(ns) || [];
        nsConnections.push(connectionToStart);
        connectionsToRefresh.set(ns, nsConnections);
      }
    });
    return connectionsToRefresh;
  }

  /**
   * Finds and returns all connections come from specified source.
   */
  getConnectionsBySourceAndEventType(
    sourceElement: RuntimeElement,
    connectionEventType: RuntimeEventType,
    workflowId?: string,
  ): Connection[] {
    const prefiltered =
      this.runtimeFacade.connections.filter(
        ({ source, eventType }) =>
          source.id === sourceElement.id &&
          source.layerType === sourceElement.layerType &&
          eventType === connectionEventType,
      ) || [];

    return !isActionEventType(connectionEventType)
      ? prefiltered
      : // NOTE pick connections with workflowId bound to action
        prefiltered.filter((c) => c.target.layerType === 'Workflow' && c.target.id === workflowId);
  }

  /**
   * @param elementId - ID of the data filter element.
   * @param namespaces - Namespaces in which connections should be refreshed when affected.
   * @returns All connections where a source workflow's 'first node has a Get node that fetches
   * the same dataset as the one which is assigned to the given data filter.
   */
  getConnectionsAffectedByDataFilter(elementId: string, namespaces: string[]): Connection[] {
    let dataFilterDatasetId: string | undefined;
    let dataFilterWorkflowId: string | undefined;

    // Check if elementId is an ID of a data filter element. If it is, keep track of datasetId and workflowId
    // associated with that data filter.
    const globalQueries = syncSelect(this.store, runtimeSelectors.selectGlobalQuery);
    for (const datasetId in globalQueries) {
      const globalQuery = globalQueries[datasetId];
      if (globalQuery?.elementId === elementId) {
        dataFilterDatasetId = globalQuery.datasetId;
        dataFilterWorkflowId = globalQuery.workflowId;
      }
    }

    if (!dataFilterDatasetId) {
      return [];
    }

    // Find all workflows which use dataset associated with the data filter in their first Get node.
    const workflows: Workflow[] = [];
    for (const workflow of this.runtimeFacade.workflows) {
      const firstNode = findFirstNodeInWorkflow(workflow);
      if (
        isGetNode(firstNode) &&
        firstNode.datasetId === dataFilterDatasetId &&
        workflow.id !== dataFilterWorkflowId
      ) {
        workflows.push(workflow);
      }
    }

    const connectionsToReloadMap = this.getDataChangedConnections(workflows);
    let connectionsToReload: Connection[] = [];
    connectionsToReloadMap.forEach((connections, ns) => {
      connections = connections.filter(({ source, target }) => {
        return target.id !== dataFilterWorkflowId && source.id !== dataFilterWorkflowId;
      });
      if (namespaces.includes(ns)) {
        connectionsToReload = connectionsToReload.concat(connections);
      }
    });

    return connectionsToReload;
  }

  getDataForElement(el: RuntimeElement, eventType?: RuntimeEventType): any {
    if (el.layerType === 'Ui') {
      const outputKey = eventType ? createElementSchemaId(el.id, eventType) : el.id;

      switch (el.output) {
        // Fallback to element id
        case 'events': {
          return (
            this.runtimeFacade.eventsUi[outputKey]?.data ?? this.runtimeFacade.eventsUi[el.id]?.data
          );
        }
        default: {
          const outputData = this.runtimeFacade.outputsUi[outputKey];
          return outputData?.data;
        }
      }
    } else if (el.layerType === 'Workflow') {
      return this.runtimeFacade.workflowOutputs[el.id];
    }
    return null;
  }

  getSchemaForRuntimeElement(
    el: RuntimeElement,
    eventType: RuntimeEventType | undefined,
  ): BlazeSchema | undefined {
    const schemaId =
      el.layerType === 'Ui' && eventType ? createElementSchemaId(el.id, eventType) : el.id;
    return getSchemaById(schemaId, this.runtimeFacade.schemas);
  }

  getEventContextForElement(
    el: RuntimeElement,
    eventType: RuntimeEventType,
  ): RuntimeEventContext | undefined {
    if (el.layerType !== 'Ui') {
      return undefined;
    }

    const outputKey = createElementSchemaId(el.id, eventType);
    const elementOutput = this.runtimeFacade.outputsUi[outputKey] ?? ({} as ElementOutputUi);
    return {
      [BULK_ACTION_IDS_PROP]: elementOutput[BULK_ACTION_IDS_PROP],
      userUpdate: elementOutput.userUpdate,
      dataAction: elementOutput.dataAction ?? this.runtimeFacade.eventsUi[outputKey]?.dataAction,
      isEmptyDataSelection: elementOutput.isEmptyDataSelection,
    };
  }

  getDatasetIdsInWorkflows(workflows: Workflow[]): string[] {
    const ids: string[] = [];
    workflows.forEach((workflow) => {
      workflow.nodes.forEach((node) => {
        if (isDatasetNode(node) && node.datasetId) {
          ids.push(node.datasetId);
        } else if (isDatasetNode(node)) {
          console.error(`Workflow ${workflow?.id} contains dataset node without dataset ID`, node);
        }
      });
    });
    return Array.from(new Set(ids));
  }

  getDatasetName(datasetNode: DatasetWorkflowNodeBase): string {
    return datasetNode?.datasetId
      ? this.runtimeFacade.datasetNames.get(datasetNode.datasetId) || ''
      : '';
  }

  getSchemasForWorkflows(workflows: Workflow[]): Observable<Record<string, DatasetSchema>> {
    const datasetIds = this.getDatasetIdsInWorkflows(workflows);
    return this.mapDatasetIdsToSchemas(datasetIds);
  }

  getSchemasForElements(elements: CanvasElement[]): Observable<Record<string, DatasetSchema>> {
    const resultMap: Record<string, boolean> = {};
    elements.forEach((e) => {
      if (!hasProps(e) || !hasDataProps(e)) {
        return;
      }
      this.getDatasetIdsInBindings(e.props.data, resultMap);
    });
    return this.mapDatasetIdsToSchemas(Object.keys(resultMap));
  }

  getSchemasFromBindings(
    data: CanvasElementDataProperties[],
  ): Observable<Record<string, DatasetSchema>> {
    return this.mapDatasetIdsToSchemas(Object.keys(this.getDatasetIdsInBindings(data)));
  }

  getTableIdsWithLinksToDataset(datasetId: string): string[] {
    const tableIds: string[] = [];
    this.schemaService.getSchemas().forEach((schema, tableId) => {
      if (schema.columns.some((column) => columnReferencesDataset(column, datasetId))) {
        tableIds.push(tableId);
      }
    });
    return tableIds;
  }

  getWorkflowPagination(workflowId: string): Pagination | undefined {
    return this.workflowPaginations.get(workflowId);
  }

  getWorkflowsToRefreshAfterDatasetChange(datasetId: string): Workflow[] {
    // Tables that link to datasetId don't depend on the workflow, so resolve them once up front.
    const linkingDatasetIds = new Set(this.getTableIdsWithLinksToDataset(datasetId));

    return this.runtimeFacade.workflows.filter((workflow) => {
      const hasNodeWithDataset = workflow.nodes.some(
        (node) =>
          (isGetNode(node) && node.datasetId === datasetId) ||
          (isJoinNode(node) && node.targetId === datasetId) ||
          (isLookupNode(node) && node.datasetId === datasetId),
      );

      // Skip workflows with a node that can trigger a data refresh of themselves, to avoid an infinite loop.
      const workflowRefreshesItself = workflow.nodes.some(
        (node) =>
          (isDeleteNode(node) && node.datasetId === datasetId) ||
          (isInsertNode(node) && node.datasetId === datasetId) ||
          (isUpdateNode(node) && node.datasetId === datasetId),
      );

      // BL-10681: skip workflows that write to a table linking to datasetId — combined with hasNodeWithDataset
      // this would loop forever.
      const workflowWritesToLinkingTable = workflow.nodes.some(
        (node) =>
          (isDeleteNode(node) || isInsertNode(node) || isUpdateNode(node)) &&
          !!node.datasetId &&
          linkingDatasetIds.has(node.datasetId),
      );

      // BL-9335: do not refresh workflows which are subscribed to websocket refresh mechanism.
      const nodeSubsToWebsocketChanges = workflow.nodes.some(
        (node) => isGetNode(node) && node.datasetId === datasetId && node.createSubscription,
      );

      return (
        hasNodeWithDataset &&
        !workflowRefreshesItself &&
        !workflowWritesToLinkingTable &&
        !nodeSubsToWebsocketChanges
      );
    });
  }

  hasInputConnections(workflow: Workflow | null, connections: Connection[]): boolean {
    return connections.some((conn) => conn.target?.id === workflow?.id);
  }

  isAutostartableNode(node?: WorkflowNode): boolean {
    return isApiCallNode(node) || isComputeNode(node) || isGetNode(node);
  }

  /**
   * Element is considered initialized when it's not explicitly set to false.
   */
  isElementInitialized(elementId: string): boolean {
    return this.runtimeFacade.initializedUiElements[elementId] === true;
  }

  /**
   * Uses saved layout and entities information to determine whether given page contains an accounts dropdown.
   * If saved information is not available, it uses endpoint to determine that.
   */
  isPageWithAccountSelector(pageId: string, isCustomerPortal?: boolean): Observable<boolean> {
    const isCustomerPortal$ = isNullish(isCustomerPortal)
      ? this.store.select(runtimeSelectors.selectIsCustomerPortal)
      : of(isCustomerPortal);
    return combineLatest({
      accountDropdownEntities: this.store.select(selectAccountsDropdownEntities),
      // We fetch all entities from the page so we can correctly detect accounts dropdown in header / footer.
      allEntities: this.store.select(selectElements),
      // allEntities: of([]),
      isCustomerPortal: isCustomerPortal$,
      loadedLayouts: this.store.select(runtimeSelectors.selectLayoutIds),
    }).pipe(
      first(),
      switchMap(({ accountDropdownEntities, allEntities, isCustomerPortal, loadedLayouts }) => {
        const layoutIds = new Set(loadedLayouts);
        // Only customer portals can have account dropdowns
        if (!isCustomerPortal) {
          return of(false);
        }

        const pageRelatedNamespaces = findRelatedNamespaces(allEntities, pageId);
        const relatedNamespaces = new Set(pageRelatedNamespaces);

        // No information has been loaded regarding this page or related namespace (header, footer).
        // Fetch accounts dropdown info using the endpoint.
        let requiredLayoutsLoaded = layoutIds.has(pageId);
        relatedNamespaces.forEach((relatedNamespace) => {
          if (!layoutIds.has(relatedNamespace)) {
            requiredLayoutsLoaded = false;
          }
        });

        if (!requiredLayoutsLoaded) {
          return this.pagesApiService.getHasAccountSelector(pageId);
        }

        for (const entity of accountDropdownEntities) {
          if (relatedNamespaces.has(entity.ns)) {
            // Found an accounts dropdown element which is present on the current or related namespaces.
            return of(true);
          }
        }
        return of(false);
      }),
    );
  }

  isPaginated(workflowId: string | undefined): boolean {
    return !!workflowId && !!this.workflowPaginations.get(workflowId);
  }

  setWorkflowPagination(workflowId: string | undefined, pagination?: Pagination): void {
    if (workflowId) {
      if (pagination) {
        this.workflowPaginations.set(workflowId, pagination);
      } else {
        this.workflowPaginations.delete(workflowId);
      }
    }
  }

  getEditableColumns(data?: CanvasElementDataProperties[]): string[] {
    const result: string[] = [];

    const activeBinding = getActiveBinding(data);

    if (!activeBinding?.dataSource?.id) {
      return result;
    }
    const inputWorkflow = this.runtimeFacade.getWorkflowById(activeBinding.dataSource.id);

    if (!inputWorkflow) {
      return result;
    }

    const firstGetNode = findFirstNodeInWorkflowOfType(inputWorkflow, isGetNode);
    const primaryDatasetId = firstGetNode?.datasetId;

    if (!primaryDatasetId) {
      return result;
    }

    const firstNodeSchema = this.schemaService.getSchema(primaryDatasetId);

    if (!firstNodeSchema) {
      return result;
    }

    const workflowSchema = this.schemaService.getWorkflowSchema(inputWorkflow.id);

    workflowSchema?.columns.forEach((colA: DatasetColumn) => {
      const column = firstNodeSchema.columns.find((colB: DatasetColumn) =>
        checkSameDatasetColumn(colA, colB),
      );
      if (column) {
        result.push(column.id ?? column.name);
      }
      if (hasSourceColumnId(colA)) {
        result.push(colA.name);
      }
    });

    return result;
  }

  async showWorkflowError({
    errorMessage,
    serverError,
    nodeId,
    workflow,
  }: {
    errorMessage?: string;
    nodeId?: string;
    serverError?: HttpErrorResponse | ServerErrorResponse;
    workflow?: Workflow | null;
  }): Promise<void> {
    let msg = getErrorMessage(serverError, errorMessage || 'An error occurred');
    if (workflow?.name) {
      msg = `[${workflow.name}] ${msg}`;
    }
    const runtimeMode = await firstValueFrom(this.store.select(runtimeSelectors.selectRuntimeMode));

    // When not in editor mode, simply show
    if (runtimeMode !== RuntimeMode.Editor) {
      this.notificationService.error(msg, true);
      return;
    }

    // In editor mode, if workflow information is known, add a link to open that workflow.
    let actionFn = undefined;
    if (workflow?.id) {
      const workflowId = workflow.id;
      actionFn = (): void => this.editorWorkflowNavigation.revealWorkflowNode(workflowId, nodeId);
    }
    this.notificationService.showNotification({
      actionFn,
      actionTextHtml: actionFn ? `<span class="uppercase underline mr-8">Review</span>` : '',
      applyTheme: true,
      message: msg,
      type: NotificationType.Error,
    });
  }

  private mapDatasetIdsToSchemas(datasetIds: string[]): Observable<Record<string, DatasetSchema>> {
    if (datasetIds.length === 0) {
      return of({});
    }
    return this.schemaService.obtainDatasetsSchemas(datasetIds);
  }

  private getDatasetIdsInBindings(
    data: CanvasElementDataProperties[],
    resultMap?: Record<string, boolean>,
  ): Record<string, boolean> {
    if (!resultMap) {
      resultMap = {};
    }
    data.forEach((binding) => {
      if (resultMap && binding.dataSource?.sourceType === 'dataset') {
        resultMap[binding.dataSource.id] = true;
      }
    });
    return resultMap;
  }
}

/**
 * Whether a column references the given dataset through any of its cross-dataset relations: a Link column,
 * a Rollup column, or a dependent (lookup-style) column. These are the backend-provided relations present on
 * the raw dataset/table schemas held by {@link SchemaService.getSchemas}; the synthetic `sourceDatasetId`
 * meta tag is deliberately excluded as it is only set on computed workflow schemas, never on those.
 */
function columnReferencesDataset(column: DatasetColumn, datasetId: string): boolean {
  return (
    column.link?.tableId === datasetId ||
    column.rollupDefinition?.tableId === datasetId ||
    !!column.dependentColumnInfos?.some((dependent) => dependent.datasetId === datasetId)
  );
}
